/**
 * FmChartTooltip — the one tooltip every chart renders.
 *
 * Recharts portals the tooltip outside the DS surface, so the frame is applied
 * inline from the `--fm-chart-tooltip-*` tokens; the body uses classes.
 */
import React from 'react';
import { FM_CHART_TOOLTIP_FRAME } from './FmChartChrome.constants';
import { useChartTooltipIntent } from './chartTooltipIntent';
import { paletteEvidenceRoleOpacity } from './palette';
import './FmChartChrome.css';

export interface FmChartTooltipEntry {
  name?: string | number;
  value?: unknown;
  color?: string;
  dataKey?: unknown;
  payload?: unknown;
}

export interface FmChartTooltipProps {
  active?: boolean;
  payload?: readonly FmChartTooltipEntry[];
  label?: string | number;
  /** Formats one value; arrays are ranges and print both edges. */
  formatValue?: (value: number) => string;
  /** Formats the category/x label at the top of the tooltip. */
  formatLabel?: (label: string | number) => string;
  /**
   * Evidence role per data key. A reference series shares its result's hue, so
   * its swatch takes the same quieter step as its bar and legend swatch.
   */
  roleByKey?: Readonly<Record<string, 'reference' | 'result' | undefined>>;
}

function formatEntryValue(value: unknown, formatValue?: (value: number) => string): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) {
    return value.map((edge) => formatEntryValue(edge, formatValue)).join('–');
  }
  if (typeof value === 'number') {
    return formatValue ? formatValue(value) : value.toLocaleString();
  }
  return String(value);
}

export const FmChartTooltip: React.FC<FmChartTooltipProps> = ({
  active,
  payload,
  label,
  formatValue,
  formatLabel,
  roleByKey,
}) => {
  const engaged = useChartTooltipIntent();
  const entries = (payload ?? []).filter(
    (entry) => entry.value !== null && entry.value !== undefined,
  );
  if (!active || !engaged || entries.length === 0) return null;
  return (
    <div className="fm-chart-tooltip" style={FM_CHART_TOOLTIP_FRAME} data-testid="fm-chart-tooltip">
      {label !== undefined && label !== '' ? (
        <div className="fm-chart-tooltip__title">
          {formatLabel ? formatLabel(label) : String(label)}
        </div>
      ) : null}
      {entries.map((entry) => (
        <div
          key={`${String(entry.dataKey ?? entry.name)}`}
          className="fm-chart-tooltip__item"
          style={
            {
              '--tooltip-swatch-color': entry.color,
              '--tooltip-swatch-opacity': paletteEvidenceRoleOpacity(
                roleByKey?.[String(entry.dataKey ?? '')],
              ),
            } as React.CSSProperties
          }
        >
          <span className="fm-chart-tooltip__swatch" aria-hidden="true" />
          <span className="fm-chart-tooltip__label">{entry.name}</span>
          <strong className="fm-chart-tooltip__value">
            {formatEntryValue(entry.value, formatValue)}
          </strong>
        </div>
      ))}
    </div>
  );
};
